import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { LinkField } from '@prismicio/client';
import { ProductCatalogService } from '@yol-digital/ms-client';
import { catchError, EMPTY, map, take } from 'rxjs';
import { UserService } from 'auth-data-access';
import { FeatureFlagService } from 'feature-flag';
import { LinkComponent } from 'link';
import { ModalsService } from 'modal';
import { ProductListItem } from 'product-shared';
import { PromotionBannerComponent } from 'promotion-banner';
import { Promotion } from 'promotion-shared';
import { ButtonComponent, ProductCardComponent } from 'sakura-ui';
import { ToastService } from 'toast';
import { TranslatePipe } from 'translate';
import { ProductBoxBase } from '../product-box-base';
import { ProductBoxAnalyticsService } from './product-box-analytics.service';
import { ProductBoxCtaComponent } from './product-box-cta/product-box-cta.component';
import { ProductBoxCtaConfigService } from './product-box-cta-config.service';
import { ProductBoxDescriptionComponent } from './product-box-description/product-box-description.component';
import { ProductBoxFooterComponent } from './product-box-footer/product-box-footer.component';
import { ProductBoxHeaderComponent } from './product-box-header/product-box-header.component';
import { ProductBoxPricingComponent } from './product-box-pricing/product-box-pricing.component';
import {
  ProductBoxPromotionSelectorModalComponent,
  ProductBoxPromotionSelectorModalData,
} from './product-box-promotion-selector/product-box-promotion-selector-modal.component';

@Component({
  selector: 'lib-product-box',
  templateUrl: './product-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    LinkComponent,
    PromotionBannerComponent,
    ProductCardComponent,
    ProductBoxHeaderComponent,
    ProductBoxDescriptionComponent,
    ProductBoxPricingComponent,
    ProductBoxCtaComponent,
    ProductBoxFooterComponent,
    TranslatePipe,
    ButtonComponent,
  ],
})
export class ProductBoxComponent extends ProductBoxBase {
  private productBoxCtaConfigService = inject(ProductBoxCtaConfigService);
  private productBoxAnalyticsService = inject(ProductBoxAnalyticsService);
  private userService = inject(UserService);
  private modalsService = inject(ModalsService);
  private toastService = inject(ToastService);
  public featureFlagService = inject(FeatureFlagService);
  private destroyRef = inject(DestroyRef);
  private readonly userType = toSignal(this.userService.getUserType());
  public productItem = input<ProductListItem>(null);
  public agentChannel = input<string>();
  public showPromotionsDialogTrigger = input(true);
  public countdownDate = input<string>();
  public override product = computed(() => this.productItem()?.product);
  private selectedPromotionOverride = signal<Promotion>(null);
  public override promotion = computed(() => this.selectedPromotionOverride() ?? this.productItem()?.promotion);
  public lightMode = input(false);
  public isNavProduct = input(false);
  public showLearnMore = input(true);
  public typeOfPageShownOn = input('');
  public ctaCustomLabel = input('');
  public ctaCustomClasses = input('');
  public ctaCustomLink = input<LinkField>();
  public cssClass = input('');
  public productDetailsLink = computed<LinkField>(() => this.productService.productDetailsLink(this.product()));
  public ctaConfig = computed(() => {
    this.translateService.translationCompleteSignal();

    const linkConfig = this.productBoxCtaConfigService.getCtaLink(
      this.product(),
      this.promotion(),
      this.lightMode(),
      this.typeOfPageShownOn(),
      this.ctaCustomLink(),
      this.productItem()?.lineCheckId
    );

    const styling = this.productBoxCtaConfigService.getCtaStyling(
      this.product(),
      this.promotion(),
      this.lightMode(),
      this.hasDiscountedCost(),
      this.ctaCustomLabel(),
      this.ctaCustomClasses(),
      this.translateService
    );

    return { ...linkConfig, ...styling };
  });
  public queryParams = computed(() => this.ctaConfig().queryParams);
  public ctaLink = computed(() => this.ctaConfig().link);
  public ctaLabel = computed(() => this.ctaConfig().label);
  public ctaClasses = computed(() => this.ctaConfig().classes);

  public onProductBoxClick() {
    this.productBoxAnalyticsService.trackProductBoxClick(
      this.product(),
      this.promotion(),
      this.ctaLink(),
      this.userType(),
      this.isNavProduct()
    );
  }

  public openPromotionsDialog() {
    const _promotions: ProductCatalogService.PosPromotionResponse[] = [];
    const dialogRef = this.modalsService.openDialog<ProductBoxPromotionSelectorModalData>(
      ProductBoxPromotionSelectorModalComponent,
      {
        data: {
          modalTitle: this.translateService.getTranslation(['product_selection', 'promotions_modal_title']),
          product: this.product(),
          currentPromotion: this.promotion(),
          chipsLabelsKeys: this.chipsLabelsKeys(),
        },
      }
    );
    const componentInstance = <ProductBoxPromotionSelectorModalComponent>dialogRef.componentInstance;

    const ziraEnabledProductClasses = this.featureFlagService
      .getFeatureValue('zira-enabled-product-classes', '')
      .split(',');

    const source = ziraEnabledProductClasses.includes(this.product().productSpecClass) ? 'zira' : 'rfe';

    this.productService
      .getAllPromotionsForProductAndChannel(this.product()?.product_code, this.agentChannel(), {
        source: source,
        productSpecClass: this.product().productSpecClass,
      })
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        map(response => {
          if ('error' in response) {
            throw new Error(response.error);
          }

          return response.promotions;
        }),
        catchError(err => {
          componentInstance.setPromotions(null);

          if (!err?.wasCaught && err?.status !== 404) {
            dialogRef.close();
            this.toastService.add(
              this.translateService.getTranslation(['product_selection', 'promotions_error_fetching_by_code']),
              false,
              1000
            );
          }

          return EMPTY;
        })
      )
      .subscribe(promotions => {
        _promotions.push(...promotions);
        const promotionModels = promotions.map(p => new Promotion(p));
        const sorted = this.productService.sortPromotionsByFinalPrice(this.product(), promotionModels);
        componentInstance.setPromotions(sorted);
      });

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((promotionId: Promotion['id']) => {
        if (promotionId) {
          const promotionOverride = new Promotion(_promotions.find(promotion => promotion.id === promotionId));
          this.selectedPromotionOverride.set(promotionOverride);
        }
      });
  }
}
